import {
  Briefcase01Icon,
  Building04Icon,
  ChartIncreaseIcon,
  Clock04Icon,
  DashboardCircleIcon,
  EarthIcon,
  HeartAddIcon,
  JobLinkIcon,
  LinkSquare01Icon,
  Location04Icon,
  Mortarboard01Icon,
  StarIcon,
} from "hugeicons-react";
import Image from "next/image";
import togetherWorkLogo from "@/assets/imgs/Logo-mini.jpg";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import { useClientTranslations } from "@/lib/translate";
import { Job } from "@/modules/types/job/job";
import { diffDate, formatCurrency } from "@/lib/utils/formatter";
import moment from "moment";
import { useEmployeeInfo } from "@/modules/hooks/employee/useEmployeeInfo";
import { useBookmarkJob } from "@/modules/hooks/job/useDetailJob";
import DisplaySkills from "@/modules/components/commons/DisplaySkills";
import { useMetadata } from "@/modules/hooks/metadata/useMetadata";

export const JobDetailsSide = ({ job }: { job: Job }) => {
  const { t } = useClientTranslations("job-info");
  const { t: tMeta } = useClientTranslations("metadata");
  const { employeeInfo } = useEmployeeInfo();
  const { onBookmarkJob } = useBookmarkJob();
  const { industries, levels, categories, degrees } = useMetadata();

  const yearExperienceFormat = (minYear: number, maxYear: number) => {
    if (maxYear <= 0) {
      if (minYear > 0) {
        return `${t("atLeast")} ${minYear} ${minYear > 1 ? t("years") : t("year")}`;
      } else {
        return t("noExperience");
      }
    }
    if (minYear === maxYear) {
      return `${minYear} ${minYear > 1 ? t("years") : t("year")}`;
    }
    if (minYear <= 0) {
      if (maxYear === Infinity) {
        return t("anyExperience");
      }
      return `${t("atLeast")} ${maxYear} ${maxYear > 1 ? t("years") : t("year")}`;
    }
    return `${minYear} - ${maxYear} ${t("years")}`;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm h-[calc(100vh-120px)] sticky top-[90px] flex flex-col">
      {/* Fixed Header Section */}
      <div className="w-full space-y-6 bg-white border-b border-gray-300 pb-5">
        <div className="flex items-start gap-4">
          <div className="relative w-20 h-20">
            <Image
              src={job.companyInfo?.companyLogo || togetherWorkLogo}
              alt={job.companyInfo?.companyName || "Together Work"}
              width={700}
              height={700}
              className="object-contain rounded-lg"
            />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex gap-2 items-center">
              {job.jobTitle}{" "}
              <Link
                href={`${routes.job.detailPath}${job.slug}`}
                target="_blank"
              >
                <LinkSquare01Icon />
              </Link>
            </h2>
            {job.companyInfo && (
              <p className="text-lg text-gray-600">
                <span className="hidden">{t("fromCompany")}</span>
                {job.companyInfo.companyName}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center text-gray-600">
            <Location04Icon className="w-5 h-5 mr-2" />
            <span>{job.address}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Briefcase01Icon className="w-5 h-5 mr-2" />
            <span>{tMeta(`jobType.${job.jobType}`)}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <Clock04Icon className="w-5 h-5 mr-2" />
            {job.createdAt && (
              <span>{diffDate(job.createdAt, moment().unix())}</span>
            )}
          </div>
          <div className="text-primary font-semibold">
            {job.isNegotiable
              ? t("salaryNegotiable")
              : job.minSalary <= 0
                ? `Up to ${formatCurrency(job.maxSalary, job.moneyUnit)}`
                : `${formatCurrency(job.minSalary, job.moneyUnit)} - ${formatCurrency(job.maxSalary, job.moneyUnit)}`}
          </div>
        </div>

        <div className="w-full flex gap-3 items-center">
          <button className="w-full bg-primary text-white py-3 max-h-fit rounded-lg hover:bg-primaryDark transition-colors duration-300">
            {t("applyNow")}
          </button>
          <button
            onClick={() => onBookmarkJob(job.id, employeeInfo?.id)}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors duration-300"
          >
            <HeartAddIcon className="text-primary size-10" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="space-y-4 overflow-y-auto px-3 py-3 custom-scrollbar">
        <p>
          <span className="font-semibold">{t("skills")}: </span>
        </p>
        <div className="w-full list-inside">
          <DisplaySkills skills={job.skills} />
        </div>
        <p className="flex items-center justify-start gap-1">
          <JobLinkIcon className="size-5" />
          <span className="font-semibold">{t("experience")}: </span>
          {yearExperienceFormat(job.minYearExperience, job.maxYearExperience)}
        </p>
        <p className="flex items-center justify-start gap-1">
          <ChartIncreaseIcon className="size-5" />
          <span className="font-semibold">{t("level")}: </span>
          {levels.find((level) => level.id === job.levelId)?.title}
        </p>
        <p className="flex items-center justify-start gap-1">
          <DashboardCircleIcon className="size-5" />
          <span className="font-semibold">{t("category")}: </span>
          {categories.find((category) => category.id === job.categoryId)?.title}
        </p>
        <p className="flex items-center justify-start gap-1">
          <Mortarboard01Icon className="size-5" />
          <span className="font-semibold">{t("educate")}: </span>
          {degrees.find((degree) => degree.id === job.degreeId)?.title}
        </p>

        <div className="w-full border-t border-gray-300 border-dashed mt-5" />
        <h3 className="text-xl font-semibold">{t("jobDescription")}</h3>
        <div
          className="text-gray-600 w-full list-inside"
          dangerouslySetInnerHTML={{ __html: job.jobDescription }}
        ></div>

        <h3 className="text-xl font-semibold">{t("requirements")}</h3>
        <div
          className="text-gray-600 w-full list-inside"
          dangerouslySetInnerHTML={{ __html: job.jobRequirement }}
        ></div>

        {job.jobResponsibility && job.jobResponsibility.length > 0 && (
          <>
            <h3 className="text-xl font-semibold">{t("responsibilities")}</h3>
            <div
              className="text-gray-600 w-full list-inside"
              dangerouslySetInnerHTML={{ __html: job.jobResponsibility }}
            ></div>
          </>
        )}

        <h3 className="text-xl font-semibold">{t("benefits")}</h3>
        <div
          className="text-gray-600 w-full list-inside"
          dangerouslySetInnerHTML={{ __html: job.jobBenefit }}
        ></div>

        {job.jobNote && job.jobNote.length > 0 && (
          <>
            <h3 className="text-xl font-semibold">{t("note")}</h3>
            <div
              className="text-gray-600 w-full list-inside"
              dangerouslySetInnerHTML={{ __html: job.jobNote }}
            ></div>
          </>
        )}

        {job.companyInfo && (
          <>
            <div className="w-full border-t border-gray-300 border-dashed mt-5" />
            <h3 className="text-xl font-semibold">{t("companyInfo")}</h3>
            <div className="text-gray-800 w-full list-inside">
              <div className="w-full space-y-2">
                <h4 className="text-lg font-semibold text-gray-800">
                  {job.companyInfo.companyName}
                </h4>
                <p className="text-gray-800 italic whitespace-pre-line">
                  {job.companyInfo.companySlogan}
                </p>
                <div className="mt-2">
                  {job.companyInfo.companyDescription && (
                    <p className="text-sm text-gray-600 mb-4">
                      {job.companyInfo.companyDescription}
                    </p>
                  )}
                </div>
              </div>
              <div className="w-full grid grid-cols-2 gap-4 mt-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Building04Icon className="w-5 h-5 text-gray-500" />
                    <span>
                      {
                        industries.find(
                          (industry) =>
                            industry.id === job.companyInfo?.industryId,
                        )?.title
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EarthIcon className="w-5 h-5 text-gray-500" />
                    <a
                      href={job.companyInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {job.companyInfo.website}
                    </a>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Briefcase01Icon className="w-5 h-5 text-gray-500" />
                    <span>
                      {tMeta(`companySize.${job.companyInfo.companySize}`)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StarIcon className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                    <span className="flex items-center">
                      {job.companyInfo.starRating.toFixed(1)}
                    </span>
                    <span className="text-gray-500">
                      ({job.companyInfo.reviewCount} {t("reviews")})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
// ⭐
JobDetailsSide.displayName = "JobDetailsSide";
